/* eslint-disable react-refresh/only-export-components */
// Router files are intentionally mixed-export: lazy component imports alongside
// the router object itself. Fast-refresh doesn't apply here.
import { Suspense, lazy, useState, useEffect, type ReactNode } from 'react';
import {
  createBrowserRouter,
  Navigate,
  useLocation,
  useRouteError,
  useSearchParams,
  type RouteObject,
  type RouterProviderProps,
} from 'react-router-dom';
import { AppShellSkeleton } from './components';
import { RequireAuth } from './components/RequireAuth';
import { RequireRole } from './components/RequireRole';
import { NotFound } from './shells/NotFound';
import { useUi } from './lib/uiStore';
import { useIsPhone } from './hooks/useIsPhone';
import { useSession } from './lib/store';
import { hydrateSession } from './lib/sessionBootstrap';
import { isChunkLoadError, reloadForFreshChunks } from './lib/chunkReload';

const SignInPage = lazy(() =>
  import('./features/auth/SignInPage').then((m) => ({ default: m.SignInPage })),
);
const SignUpPage = lazy(() =>
  import('./features/auth/SignUpPage').then((m) => ({ default: m.SignUpPage })),
);
const ResetPasswordPage = lazy(() =>
  import('./features/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const MembersAdminPage = lazy(() =>
  import('./features/members/MembersAdminPage').then((m) => ({ default: m.MembersAdminPage })),
);
const SubscribePage = lazy(() =>
  import('./features/subscribe/SubscribePage').then((m) => ({ default: m.SubscribePage })),
);
const PlanAndBillingPage = lazy(() =>
  import('./features/settings/PlanAndBillingPage').then((m) => ({
    default: m.PlanAndBillingPage,
  })),
);

const DesktopLayout = lazy(() =>
  import('./shells/desktop/DesktopLayout').then((m) => ({ default: m.DesktopLayout })),
);
const DesktopHome = lazy(() =>
  import('./shells/desktop/DesktopHome').then((m) => ({ default: m.DesktopHome })),
);
const InventoryPage = lazy(() =>
  import('./features/inventory/InventoryPage').then((m) => ({ default: m.InventoryPage })),
);
const DesktopSalesPlaceholder = lazy(() =>
  import('./shells/desktop/placeholders').then((m) => ({
    default: m.DesktopSalesPlaceholder,
  })),
);
const SalesListPage = lazy(() =>
  import('./features/sales/SalesListPage').then((m) => ({ default: m.SalesListPage })),
);
const SaleIntakePage = lazy(() =>
  import('./features/sales/SaleIntakePage').then((m) => ({ default: m.SaleIntakePage })),
);
const LocationsListPage = lazy(() =>
  import('./features/locations/LocationsListPage').then((m) => ({
    default: m.LocationsListPage,
  })),
);
const DefaultLocationPickerPage = lazy(() =>
  import('./features/settings/DefaultLocationPickerPage').then((m) => ({
    default: m.DefaultLocationPickerPage,
  })),
);
const SettingsHome = lazy(() =>
  import('./features/settings/SettingsHome').then((m) => ({ default: m.SettingsHome })),
);
const BarcodeSequencePage = lazy(() =>
  import('./features/settings/BarcodeSequencePage').then((m) => ({
    default: m.BarcodeSequencePage,
  })),
);
const PricingRulesPage = lazy(() =>
  import('./features/settings/PricingRulesPage').then((m) => ({
    default: m.PricingRulesPage,
  })),
);
const KioskSettingsPage = lazy(() =>
  import('./features/settings/KioskSettingsPage').then((m) => ({
    default: m.KioskSettingsPage,
  })),
);
const AlertSettingsPage = lazy(() =>
  import('./features/settings/AlertSettingsPage').then((m) => ({
    default: m.AlertSettingsPage,
  })),
);
const AppearanceSettingsPage = lazy(() =>
  import('./features/settings/AppearanceSettingsPage').then((m) => ({
    default: m.AppearanceSettingsPage,
  })),
);
const ConsignmentSettingsPage = lazy(() =>
  import('./features/settings/ConsignmentSettingsPage').then((m) => ({
    default: m.ConsignmentSettingsPage,
  })),
);
const AnalyticsSettingsPage = lazy(() =>
  import('./features/settings/AnalyticsSettingsPage').then((m) => ({
    default: m.AnalyticsSettingsPage,
  })),
);
const PublicPagesSettingsPage = lazy(() =>
  import('./features/settings/PublicPagesSettingsPage').then((m) => ({
    default: m.PublicPagesSettingsPage,
  })),
);
const KioskDisplaySettingsPage = lazy(() =>
  import('./features/settings/KioskDisplaySettingsPage').then((m) => ({
    default: m.KioskDisplaySettingsPage,
  })),
);
const IntakeSettingsPage = lazy(() =>
  import('./features/settings/IntakeSettingsPage').then((m) => ({
    default: m.IntakeSettingsPage,
  })),
);
const AnalyticsPage = lazy(() =>
  import('./features/analytics/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })),
);
const AlertsPage = lazy(() =>
  import('./features/alerts/AlertsPage').then((m) => ({ default: m.AlertsPage })),
);
const BinsListPage = lazy(() =>
  import('./features/bins/BinsListPage').then((m) => ({ default: m.BinsListPage })),
);
const BinContentsPage = lazy(() =>
  import('./features/bins/BinContentsPage').then((m) => ({ default: m.BinContentsPage })),
);
const DisplayCasesListPage = lazy(() =>
  import('./features/display-cases/DisplayCasesListPage').then((m) => ({
    default: m.DisplayCasesListPage,
  })),
);
const DisplayCaseContentsPage = lazy(() =>
  import('./features/display-cases/DisplayCaseContentsPage').then((m) => ({
    default: m.DisplayCaseContentsPage,
  })),
);
// Slab Binders is removed for the v2.0 launch — graded slabs now live in Display
// Cases. The feature + components remain in the repo; restore the lazy imports and
// the two routes below (and the navConfig entry) to bring the container back.
// const SlabBindersListPage = lazy(() =>
//   import('./features/slab-binders/SlabBindersListPage').then((m) => ({
//     default: m.SlabBindersListPage,
//   })),
// );
// const SlabBinderContentsPage = lazy(() =>
//   import('./features/slab-binders/SlabBinderContentsPage').then((m) => ({
//     default: m.SlabBinderContentsPage,
//   })),
// );
const BindersListPage = lazy(() =>
  import('./features/binders/BindersListPage').then((m) => ({
    default: m.BindersListPage,
  })),
);
const BinderContentsPage = lazy(() =>
  import('./features/binders/BinderContentsPage').then((m) => ({
    default: m.BinderContentsPage,
  })),
);
const SealedStockListPage = lazy(() =>
  import('./features/sealed-stock/SealedStockListPage').then((m) => ({
    default: m.SealedStockListPage,
  })),
);
// Sealed Displays (position-aware fixtures) is hidden for the v2.0 launch — the
// feature is backed only by an in-memory demo store with no backend table, so it
// would lose data on refresh. The components + shared `displays/sealedCatalog`
// (used by the real Sealed Stock picker) remain; restore the two routes below
// once a backing table + RPCs land. See docs/status.md "Open items".
// const SealedDisplaysListPage = lazy(() =>
//   import('./features/sealed-stock/displays/SealedDisplaysListPage').then((m) => ({
//     default: m.SealedDisplaysListPage,
//   })),
// );
// const SealedDisplayDetailPage = lazy(() =>
//   import('./features/sealed-stock/displays/SealedDisplayDetailPage').then((m) => ({
//     default: m.SealedDisplayDetailPage,
//   })),
// );
const AcquisitionsListPage = lazy(() =>
  import('./features/acquisitions/AcquisitionsListPage').then((m) => ({
    default: m.AcquisitionsListPage,
  })),
);
const AcquisitionDetailPage = lazy(() =>
  import('./features/acquisitions/AcquisitionDetailPage').then((m) => ({
    default: m.AcquisitionDetailPage,
  })),
);
const AcquisitionIntakePage = lazy(() =>
  import('./features/acquisitions/AcquisitionIntakePage').then((m) => ({
    default: m.AcquisitionIntakePage,
  })),
);
const BuyIntakePage = lazy(() =>
  import('./features/acquisitions/BuyIntakePage').then((m) => ({
    default: m.BuyIntakePage,
  })),
);
const TradeIntakePage = lazy(() =>
  import('./features/acquisitions/TradeIntakePage').then((m) => ({
    default: m.TradeIntakePage,
  })),
);
const ConsignmentIntakePage = lazy(() =>
  import('./features/acquisitions/ConsignmentIntakePage').then((m) => ({
    default: m.ConsignmentIntakePage,
  })),
);
const TransferPage = lazy(() =>
  import('./features/transfers/TransferPage').then((m) => ({ default: m.TransferPage })),
);
const LocationReportPage = lazy(() =>
  import('./features/locations/LocationReportPage').then((m) => ({
    default: m.LocationReportPage,
  })),
);
const ConsignorsListPage = lazy(() =>
  import('./features/consignment/ConsignorsListPage').then((m) => ({
    default: m.ConsignorsListPage,
  })),
);
const ConsignorReportPage = lazy(() =>
  import('./features/consignment/ConsignorReportPage').then((m) => ({
    default: m.ConsignorReportPage,
  })),
);
const ShopConsignorReportPage = lazy(() =>
  import('./features/consignment/ShopConsignorReportPage').then((m) => ({
    default: m.ShopConsignorReportPage,
  })),
);
const ConsignorPayoutsPage = lazy(() =>
  import('./features/consignment/ConsignorPayoutsPage').then((m) => ({
    default: m.ConsignorPayoutsPage,
  })),
);
const EventsPage = lazy(() =>
  import('./features/events/EventsPage').then((m) => ({ default: m.EventsPage })),
);
const EventDetailPage = lazy(() =>
  import('./features/events/EventDetailPage').then((m) => ({ default: m.EventDetailPage })),
);
const EventsReportPage = lazy(() =>
  import('./features/events/EventsReportPage').then((m) => ({ default: m.EventsReportPage })),
);
const ReportsHubPage = lazy(() =>
  import('./features/reports/ReportsHubPage').then((m) => ({ default: m.ReportsHubPage })),
);
const DowReportPage = lazy(() =>
  import('./features/reports/DowReportPage').then((m) => ({ default: m.DowReportPage })),
);
const HodReportPage = lazy(() =>
  import('./features/reports/HodReportPage').then((m) => ({ default: m.HodReportPage })),
);
const MomReportPage = lazy(() =>
  import('./features/reports/MomReportPage').then((m) => ({ default: m.MomReportPage })),
);
const TopMoversReportPage = lazy(() =>
  import('./features/reports/TopMoversReportPage').then((m) => ({
    default: m.TopMoversReportPage,
  })),
);
const LocationMovementPage = lazy(() =>
  import('./features/reports/LocationMovementPage').then((m) => ({
    default: m.LocationMovementPage,
  })),
);
const ConsignorPeriodReportPage = lazy(() =>
  import('./features/reports/ConsignorPeriodReportPage').then((m) => ({
    default: m.ConsignorPeriodReportPage,
  })),
);
const PlSummaryPage = lazy(() =>
  import('./features/pl/PlSummaryPage').then((m) => ({ default: m.PlSummaryPage })),
);
const UnrealizedPlPage = lazy(() =>
  import('./features/pl/UnrealizedPlPage').then((m) => ({ default: m.UnrealizedPlPage })),
);
const AuditSessionPage = lazy(() =>
  import('./features/audit/AuditSessionPage').then((m) => ({ default: m.AuditSessionPage })),
);
const AuditDiffPage = lazy(() =>
  import('./features/audit/AuditDiffPage').then((m) => ({ default: m.AuditDiffPage })),
);
const ProvenancePage = lazy(() =>
  import('./features/provenance-tree/ProvenancePage').then((m) => ({ default: m.ProvenancePage })),
);
const VaultPage = lazy(() =>
  import('./features/vault/VaultPage').then((m) => ({ default: m.VaultPage })),
);
const ImportPage = lazy(() =>
  import('./features/csv-import/ImportPage').then((m) => ({ default: m.ImportPage })),
);
const CustomersListPage = lazy(() =>
  import('./features/customers/CustomersListPage').then((m) => ({
    default: m.CustomersListPage,
  })),
);
const CustomerDetailPage = lazy(() =>
  import('./features/customers/CustomerDetailPage').then((m) => ({
    default: m.CustomerDetailPage,
  })),
);
const CardDetailPage = lazy(() =>
  import('./features/scanner/CardDetailPage').then((m) => ({ default: m.CardDetailPage })),
);
const StackDetailPage = lazy(() =>
  import('./features/scanner/StackDetailPage').then((m) => ({ default: m.StackDetailPage })),
);

// Dev-only: design system reference page. The lazy import lives inside the
// if-block so Vite's dead-code elimination strips the dynamic-import site
// from production builds when import.meta.env.DEV is false.
const devRoutes: RouteObject[] = [];
if (import.meta.env.DEV) {
  const DesignSystemPage = lazy(() =>
    import('./features/dev/DesignSystemPage').then((m) => ({ default: m.DesignSystemPage })),
  );
  devRoutes.push({
    path: 'dev/design-system',
    element: withSuspense(<DesignSystemPage />),
  });
}

const KioskLayout = lazy(() =>
  import('./shells/kiosk/KioskLayout').then((m) => ({ default: m.KioskLayout })),
);
const KioskHome = lazy(() =>
  import('./shells/kiosk/KioskHome').then((m) => ({ default: m.KioskHome })),
);
const KioskDisplayCasesPage = lazy(() =>
  import('./features/kiosk/KioskDisplayCasesPage').then((m) => ({
    default: m.KioskDisplayCasesPage,
  })),
);
const KioskCaseDetailPage = lazy(() =>
  import('./features/kiosk/KioskCaseDetailPage').then((m) => ({ default: m.KioskCaseDetailPage })),
);
const KioskBindersPage = lazy(() =>
  import('./features/kiosk/KioskBindersPage').then((m) => ({ default: m.KioskBindersPage })),
);
const KioskBinderDetailPage = lazy(() =>
  import('./features/kiosk/KioskBinderDetailPage').then((m) => ({
    default: m.KioskBinderDetailPage,
  })),
);
// Slab Binders removed for v2.0 launch (see desktop note above) — kiosk surfaces hidden too.
// const KioskSlabBindersPage = lazy(() =>
//   import('./features/kiosk/KioskSlabBindersPage').then((m) => ({
//     default: m.KioskSlabBindersPage,
//   })),
// );
// const KioskSlabBinderDetailPage = lazy(() =>
//   import('./features/kiosk/KioskSlabBinderDetailPage').then((m) => ({
//     default: m.KioskSlabBinderDetailPage,
//   })),
// );
const KioskSealedPage = lazy(() =>
  import('./features/kiosk/KioskSealedPage').then((m) => ({ default: m.KioskSealedPage })),
);
const KioskSearchPage = lazy(() =>
  import('./features/kiosk/KioskSearchPage').then((m) => ({ default: m.KioskSearchPage })),
);
const KioskShowBoxesPage = lazy(() =>
  import('./features/kiosk/KioskShowBoxesPage').then((m) => ({ default: m.KioskShowBoxesPage })),
);
const KioskShowBoxDetailPage = lazy(() =>
  import('./features/kiosk/KioskShowBoxDetailPage').then((m) => ({
    default: m.KioskShowBoxDetailPage,
  })),
);
const KioskFeaturedView = lazy(() =>
  import('./features/kiosk/display/KioskExtraViews').then((m) => ({
    default: m.KioskFeaturedView,
  })),
);
const KioskShowsView = lazy(() =>
  import('./features/kiosk/display/KioskExtraViews').then((m) => ({ default: m.KioskShowsView })),
);
const KioskConnectView = lazy(() =>
  import('./features/kiosk/display/KioskExtraViews').then((m) => ({ default: m.KioskConnectView })),
);
const KioskPayView = lazy(() =>
  import('./features/kiosk/display/KioskExtraViews').then((m) => ({ default: m.KioskPayView })),
);
const KioskJoinView = lazy(() =>
  import('./features/kiosk/display/KioskExtraViews').then((m) => ({ default: m.KioskJoinView })),
);

const MobileLayout = lazy(() =>
  import('./shells/mobile/MobileLayout').then((m) => ({ default: m.MobileLayout })),
);
const MobileHome = lazy(() =>
  import('./shells/mobile/MobileHome').then((m) => ({ default: m.MobileHome })),
);
const MobileLookup = lazy(() =>
  import('./shells/mobile/MobileLookup').then((m) => ({ default: m.MobileLookup })),
);
const MobileEditPage = lazy(() =>
  import('./shells/mobile/MobileEditPage').then((m) => ({
    default: m.MobileEditPage,
  })),
);
const MobileCardDetailPage = lazy(() =>
  import('./shells/mobile/MobileCardDetailPage').then((m) => ({
    default: m.MobileCardDetailPage,
  })),
);
const MobileStackDetailPage = lazy(() =>
  import('./shells/mobile/MobileStackDetailPage').then((m) => ({
    default: m.MobileStackDetailPage,
  })),
);
const MobileMorePage = lazy(() =>
  import('./shells/mobile/MobileMorePage').then((m) => ({ default: m.MobileMorePage })),
);
const MobileHelpPage = lazy(() =>
  import('./shells/mobile/MobileHelpPage').then((m) => ({ default: m.MobileHelpPage })),
);
const MobileInventoryPage = lazy(() =>
  import('./shells/mobile/MobileInventoryPage').then((m) => ({
    default: m.MobileInventoryPage,
  })),
);
const MobileAddResearchPage = lazy(() =>
  import('./shells/mobile/MobileAddResearchPage').then((m) => ({
    default: m.MobileAddResearchPage,
  })),
);
const MobileSalesPage = lazy(() =>
  import('./shells/mobile/MobileSalesPage').then((m) => ({ default: m.MobileSalesPage })),
);
const MobileAlertsPage = lazy(() =>
  import('./shells/mobile/MobileAlertsPage').then((m) => ({ default: m.MobileAlertsPage })),
);
const MobileAcquisitionsPage = lazy(() =>
  import('./shells/mobile/MobileAcquisitionsPage').then((m) => ({
    default: m.MobileAcquisitionsPage,
  })),
);
const MobileReportsPage = lazy(() =>
  import('./shells/mobile/MobileReportsPage').then((m) => ({ default: m.MobileReportsPage })),
);
const MobileTopMoversPage = lazy(() =>
  import('./shells/mobile/MobileTopMoversPage').then((m) => ({
    default: m.MobileTopMoversPage,
  })),
);
const MobileBinsPage = lazy(() =>
  import('./shells/mobile/MobileBinsPage').then((m) => ({ default: m.MobileBinsPage })),
);
const MobileCasesPage = lazy(() =>
  import('./shells/mobile/MobileCasesPage').then((m) => ({ default: m.MobileCasesPage })),
);
const MobileBindersPage = lazy(() =>
  import('./shells/mobile/MobileBindersPage').then((m) => ({ default: m.MobileBindersPage })),
);
const MobileSealedPage = lazy(() =>
  import('./shells/mobile/MobileSealedPage').then((m) => ({ default: m.MobileSealedPage })),
);
const MobileVaultPage = lazy(() =>
  import('./shells/mobile/MobileVaultPage').then((m) => ({ default: m.MobileVaultPage })),
);
const MobileEventsPage = lazy(() =>
  import('./shells/mobile/MobileEventsPage').then((m) => ({ default: m.MobileEventsPage })),
);
const MobileCustomersPage = lazy(() =>
  import('./shells/mobile/MobileCustomersPage').then((m) => ({
    default: m.MobileCustomersPage,
  })),
);
const MobileConsignorsPage = lazy(() =>
  import('./shells/mobile/MobileConsignorsPage').then((m) => ({
    default: m.MobileConsignorsPage,
  })),
);
const MobileConsignorReportPage = lazy(() =>
  import('./shells/mobile/MobileConsignorReportPage').then((m) => ({
    default: m.MobileConsignorReportPage,
  })),
);
const MobileAnalyticsPage = lazy(() =>
  import('./shells/mobile/MobileAnalyticsPage').then((m) => ({
    default: m.MobileAnalyticsPage,
  })),
);
const MobileAuditPage = lazy(() =>
  import('./shells/mobile/MobileAuditPage').then((m) => ({ default: m.MobileAuditPage })),
);
const MobilePlPage = lazy(() =>
  import('./shells/mobile/MobilePlPage').then((m) => ({ default: m.MobilePlPage })),
);
const MobileImportPage = lazy(() =>
  import('./shells/mobile/MobileImportPage').then((m) => ({ default: m.MobileImportPage })),
);
const MobileMembersPage = lazy(() =>
  import('./shells/mobile/MobileMembersPage').then((m) => ({ default: m.MobileMembersPage })),
);
const MobileSettingsPage = lazy(() =>
  import('./shells/mobile/MobileSettingsPage').then((m) => ({
    default: m.MobileSettingsPage,
  })),
);
const MobileTransferPage = lazy(() =>
  import('./shells/mobile/MobileTransferPage').then((m) => ({
    default: m.MobileTransferPage,
  })),
);
const MobileShowPicker = lazy(() =>
  import('./shells/mobile/show/MobileShowPicker').then((m) => ({
    default: m.MobileShowPicker,
  })),
);
const MobileShowHub = lazy(() =>
  import('./shells/mobile/show/MobileShowHub').then((m) => ({
    default: m.MobileShowHub,
  })),
);
const MobileQuickSell = lazy(() =>
  import('./shells/mobile/show/MobileQuickSell').then((m) => ({
    default: m.MobileQuickSell,
  })),
);
const MobileQuickBuy = lazy(() =>
  import('./shells/mobile/show/MobileQuickBuy').then((m) => ({
    default: m.MobileQuickBuy,
  })),
);
const MobileQuickTrade = lazy(() =>
  import('./shells/mobile/show/MobileQuickTrade').then((m) => ({
    default: m.MobileQuickTrade,
  })),
);

// Prototype routes — only registered when VITE_SHOW_PROTOTYPES=true. The lazy
// imports live inside the if-block so Vite's dead-code elimination strips the
// dynamic-import sites from prod builds when the env var is unset.
// See docs/spikes/01-4-ux.md.
const SHOW_PROTOTYPES = import.meta.env.VITE_SHOW_PROTOTYPES === 'true';

const prototypeRoutes: RouteObject[] = [];
const desktopPrototypeRoutes: RouteObject[] = [];
if (SHOW_PROTOTYPES) {
  const ScanToAddPrototype = lazy(() =>
    import('./prototypes/m/scan-to-add').then((m) => ({ default: m.ScanToAddPrototype })),
  );
  const ScanToFindPrototype = lazy(() =>
    import('./prototypes/m/scan-to-find').then((m) => ({ default: m.ScanToFindPrototype })),
  );
  const ScannerDemoPrototype = lazy(() =>
    import('./prototypes/scanner-demo').then((m) => ({ default: m.ScannerDemoPrototype })),
  );
  prototypeRoutes.push(
    { path: 'proto/scan-to-add', element: withSuspense(<ScanToAddPrototype />) },
    { path: 'proto/scan-to-find', element: withSuspense(<ScanToFindPrototype />) },
    { path: 'proto/scanner-demo', element: withSuspense(<ScannerDemoPrototype />) },
  );
  desktopPrototypeRoutes.push({
    path: 'proto/scanner-demo',
    element: withSuspense(<ScannerDemoPrototype />),
  });
}

function withSuspense(node: ReactNode) {
  return <Suspense fallback={<AppShellSkeleton />}>{node}</Suspense>;
}

/** Wraps a route element in a manager-or-owner role gate. */
function managerUp(node: ReactNode) {
  return <RequireRole minRole="manager">{withSuspense(node)}</RequireRole>;
}

/** Wraps a route element in an owner-only role gate. */
function ownerOnly(node: ReactNode) {
  return <RequireRole minRole="owner">{withSuspense(node)}</RequireRole>;
}

/**
 * Redirects authenticated users without a shop to /subscribe.
 *
 * When returning from Stripe Checkout (?subscribed=1), the webhook that creates
 * the shop row is async — the redirect can arrive before the shop exists. We
 * detect that param and poll hydrateSession every 2 s (up to 30 s) so the user
 * lands on the dashboard rather than being bounced back to /subscribe.
 */
function RequireShop({ children }: { children: ReactNode }) {
  const currentShopId = useSession((s) => s.currentShopId);
  const userId = useSession((s) => s.userId);
  const email = useSession((s) => s.email);
  const status = useSession((s) => s.status);
  const [searchParams, setSearchParams] = useSearchParams();
  const subscribed = searchParams.get('subscribed') === '1';
  const [timedOut, setTimedOut] = useState(false);

  // Poll hydrateSession while waiting for the Stripe webhook to create the shop.
  // Interval callback is async so setState calls inside it satisfy the linter.
  useEffect(() => {
    if (!subscribed || currentShopId || timedOut || !userId || !email) return;
    let attempts = 0;
    const id = setInterval(() => {
      attempts++;
      void hydrateSession(userId, email);
      if (attempts >= 15) {
        clearInterval(id);
        setTimedOut(true);
      }
    }, 2000);
    return () => clearInterval(id);
  }, [subscribed, currentShopId, timedOut, userId, email]);

  // Strip ?subscribed=1 from the URL once the shop is confirmed.
  useEffect(() => {
    if (subscribed && currentShopId) {
      setSearchParams(
        (p) => {
          p.delete('subscribed');
          return p;
        },
        { replace: true },
      );
    }
  }, [subscribed, currentShopId, setSearchParams]);

  if (status === 'loading') return <AppShellSkeleton />;
  // Show skeleton while waiting for the webhook (subscribed=1, no shop yet, not timed out).
  if (subscribed && !currentShopId && !timedOut) return <AppShellSkeleton />;
  if (!currentShopId) {
    if (timedOut) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            fontFamily: 'Inter, system-ui, sans-serif',
          }}
        >
          <div style={{ textAlign: 'center', maxWidth: 400, padding: 32 }}>
            <p style={{ marginBottom: 8, fontWeight: 600 }}>Still setting up your shop…</p>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 24 }}>
              Your payment was received. Please refresh in a moment, or contact support if this
              persists.
            </p>
            <button
              onClick={() => window.location.replace('/?subscribed=1')}
              style={{
                padding: '10px 24px',
                borderRadius: 8,
                border: '1px solid #ccc',
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              Refresh
            </button>
          </div>
        </div>
      );
    }
    return <Navigate to="/subscribe" replace />;
  }
  return <>{children}</>;
}

/**
 * Desktop index route (the app entry at `/`).
 *
 *   - On an actual phone (narrow + touch, via {@link useIsPhone}) it redirects
 *     into the (mobile) shell at `/m/home` — so opening the app on a phone lands
 *     on the mobile dashboard automatically. This fires ONLY at `/`, so a phone
 *     deep-link to a specific desktop page (e.g. `/inventory`) still works.
 *   - Otherwise it honors the user's `defaultLandingPath` preference: the
 *     dashboard (`/`) renders inline; any other choice issues a one-hop redirect.
 *     The allow-list in `uiStore` guarantees the target is a real route, so this
 *     can never loop or 404.
 */
function DesktopIndex() {
  const isPhone = useIsPhone();
  const landing = useUi((s) => s.defaultLandingPath);
  if (isPhone) return <Navigate to="/m/home" replace />;
  if (landing && landing !== '/') return <Navigate to={landing} replace />;
  return <DesktopHome />;
}

/**
 * Redirects /find (and /find?q=term) to /inventory (and /inventory?q=term).
 * Preserves the ?q= param so Spotlight deep-links and any existing bookmarks
 * continue to work after the two pages were merged.
 */
function FindRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/inventory${search}`} replace />;
}

/**
 * Route-level error boundary. When a lazy route chunk fails to load — the
 * signature of a stale content-hashed bundle after a deploy — it reloads once
 * to pull the fresh build (see {@link reloadForFreshChunks}); the
 * `vite:preloadError` listener in main.tsx usually catches this first, so this
 * is the safety net for failures that surface as a render-time route error
 * (e.g. on the public auth routes). Any other routing/render error falls back
 * to the standard NotFound surface.
 */
function RouteErrorBoundary() {
  const error = useRouteError();
  const isChunk = isChunkLoadError(error);
  useEffect(() => {
    if (isChunk) reloadForFreshChunks();
  }, [isChunk]);
  if (isChunk) return <AppShellSkeleton />;
  return <NotFound />;
}

export const router: RouterProviderProps['router'] = createBrowserRouter([
  // ----- public auth surfaces (no RequireAuth gate) ------------------------
  // errorElement recovers a stale lazy chunk after a deploy: these routes have
  // no parent boundary, so a failed import would otherwise crash the SPA.
  { path: '/sign-in', element: withSuspense(<SignInPage />), errorElement: <RouteErrorBoundary /> },
  { path: '/sign-up', element: withSuspense(<SignUpPage />), errorElement: <RouteErrorBoundary /> },
  {
    path: '/reset-password',
    element: withSuspense(<ResetPasswordPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/subscribe',
    element: <RequireAuth>{withSuspense(<SubscribePage />)}</RequireAuth>,
    errorElement: <RouteErrorBoundary />,
  },

  {
    path: '/',
    element: (
      <RequireAuth>
        <RequireShop>
          <Suspense fallback={<AppShellSkeleton />}>
            <DesktopLayout />
          </Suspense>
        </RequireShop>
      </RequireAuth>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: withSuspense(<DesktopIndex />) },
      { path: 'alerts', element: withSuspense(<AlertsPage />) },
      { path: 'inventory', element: withSuspense(<InventoryPage />) },
      { path: 'sales', element: withSuspense(<SalesListPage />) },
      { path: 'sales/new', element: withSuspense(<SaleIntakePage />) },
      { path: 'sales/:saleId', element: withSuspense(<AcquisitionDetailPage />) },
      { path: 'bins', element: withSuspense(<BinsListPage />) },
      { path: 'bins/:binId/contents', element: withSuspense(<BinContentsPage />) },
      { path: 'cases', element: withSuspense(<DisplayCasesListPage />) },
      {
        path: 'cases/:caseId/contents',
        element: withSuspense(<DisplayCaseContentsPage />),
      },
      { path: 'sealed-stock', element: withSuspense(<SealedStockListPage />) },
      // Sealed Displays routes hidden for v2.0 launch (in-memory demo only — no backend).
      // { path: 'sealed-displays', element: withSuspense(<SealedDisplaysListPage />) },
      // {
      //   path: 'sealed-displays/:displayId',
      //   element: withSuspense(<SealedDisplayDetailPage />),
      // },
      // Slab Binders routes hidden for v2.0 launch (slabs live in Display Cases now).
      // { path: 'slab-binders', element: withSuspense(<SlabBindersListPage />) },
      // {
      //   path: 'slab-binders/:binderId/contents',
      //   element: withSuspense(<SlabBinderContentsPage />),
      // },
      { path: 'binders', element: withSuspense(<BindersListPage />) },
      {
        path: 'binders/:binderId/contents',
        element: withSuspense(<BinderContentsPage />),
      },
      { path: 'acquisitions', element: withSuspense(<AcquisitionsListPage />) },
      { path: 'acquisitions/new', element: withSuspense(<AcquisitionIntakePage />) },
      { path: 'acquisitions/new/buy', element: withSuspense(<BuyIntakePage />) },
      { path: 'acquisitions/new/trade', element: withSuspense(<TradeIntakePage />) },
      { path: 'acquisitions/new/consignment', element: withSuspense(<ConsignmentIntakePage />) },
      { path: 'acquisitions/:acquisitionId', element: withSuspense(<AcquisitionDetailPage />) },
      { path: 'transfers/new', element: withSuspense(<TransferPage />) },
      { path: 'find', element: <FindRedirect /> },
      { path: 'instances/:instanceId', element: withSuspense(<CardDetailPage />) },
      { path: 'stacks/:skuStockId', element: withSuspense(<StackDetailPage />) },
      { path: 'locations', element: <Navigate to="/settings/locations" replace /> },
      { path: 'locations/:locationId/report', element: managerUp(<LocationReportPage />) },
      { path: 'customers', element: withSuspense(<CustomersListPage />) },
      { path: 'customers/:customerId', element: withSuspense(<CustomerDetailPage />) },
      { path: 'consignors', element: withSuspense(<ConsignorsListPage />) },
      { path: 'consignors/payouts', element: managerUp(<ConsignorPayoutsPage />) },
      { path: 'consignors/:consignorId/report', element: withSuspense(<ConsignorReportPage />) },
      {
        path: 'consignors/:consignorId/shop-report',
        element: managerUp(<ShopConsignorReportPage />),
      },
      { path: 'events', element: withSuspense(<EventsPage />) },
      { path: 'events/calendar', element: <Navigate to="/events" replace /> },
      { path: 'events/:eventId', element: withSuspense(<EventDetailPage />) },
      { path: 'events/report', element: withSuspense(<EventsReportPage />) },
      { path: 'analytics', element: managerUp(<AnalyticsPage />) },
      { path: 'reports', element: managerUp(<ReportsHubPage />) },
      { path: 'reports/dow', element: managerUp(<DowReportPage />) },
      { path: 'reports/hod', element: managerUp(<HodReportPage />) },
      { path: 'reports/mom', element: managerUp(<MomReportPage />) },
      { path: 'reports/top-movers', element: managerUp(<TopMoversReportPage />) },
      {
        path: 'reports/location/:locationId',
        element: managerUp(<LocationMovementPage />),
      },
      {
        path: 'reports/consignor/:consignorId',
        element: managerUp(<ConsignorPeriodReportPage />),
      },
      { path: 'pl', element: ownerOnly(<PlSummaryPage />) },
      { path: 'pl/unrealized', element: ownerOnly(<UnrealizedPlPage />) },
      { path: 'labels', element: withSuspense(<DesktopSalesPlaceholder />) },
      { path: 'import', element: ownerOnly(<ImportPage />) },
      { path: 'inventory/import', element: ownerOnly(<ImportPage />) },
      { path: 'audit', element: managerUp(<AuditSessionPage />) },
      { path: 'audit/:sessionId', element: managerUp(<AuditSessionPage />) },
      { path: 'audit/:sessionId/diff', element: managerUp(<AuditDiffPage />) },
      { path: 'provenance/:instanceId', element: withSuspense(<ProvenancePage />) },
      { path: 'vault', element: ownerOnly(<VaultPage />) },
      { path: 'settings', element: withSuspense(<SettingsHome />) },
      { path: 'settings/locations', element: ownerOnly(<LocationsListPage />) },
      { path: 'settings/default-location', element: managerUp(<DefaultLocationPickerPage />) },
      { path: 'settings/members', element: ownerOnly(<MembersAdminPage />) },
      { path: 'settings/billing', element: ownerOnly(<PlanAndBillingPage />) },
      { path: 'settings/barcode', element: ownerOnly(<BarcodeSequencePage />) },
      { path: 'settings/pricing', element: ownerOnly(<PricingRulesPage />) },
      { path: 'settings/alerts', element: managerUp(<AlertSettingsPage />) },
      { path: 'settings/appearance', element: withSuspense(<AppearanceSettingsPage />) },
      { path: 'settings/kiosk', element: managerUp(<KioskSettingsPage />) },
      { path: 'settings/kiosk-display', element: managerUp(<KioskDisplaySettingsPage />) },
      { path: 'settings/intake', element: managerUp(<IntakeSettingsPage />) },
      { path: 'settings/consignment', element: managerUp(<ConsignmentSettingsPage />) },
      { path: 'settings/analytics', element: managerUp(<AnalyticsSettingsPage />) },
      { path: 'settings/public-pages', element: managerUp(<PublicPagesSettingsPage />) },
      ...desktopPrototypeRoutes,
      ...devRoutes,
    ],
  },
  {
    path: '/kiosk',
    element: withSuspense(<KioskLayout />),
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: withSuspense(<KioskHome />) },
      { path: 'cases', element: withSuspense(<KioskDisplayCasesPage />) },
      { path: 'cases/:caseId', element: withSuspense(<KioskCaseDetailPage />) },
      { path: 'binders', element: withSuspense(<KioskBindersPage />) },
      { path: 'binders/:binderId', element: withSuspense(<KioskBinderDetailPage />) },
      // Slab Binders kiosk routes hidden for v2.0 launch (slabs live in Display Cases now).
      // { path: 'slab-binders', element: withSuspense(<KioskSlabBindersPage />) },
      // { path: 'slab-binders/:binderId', element: withSuspense(<KioskSlabBinderDetailPage />) },
      { path: 'sealed', element: withSuspense(<KioskSealedPage />) },
      { path: 'show-boxes', element: withSuspense(<KioskShowBoxesPage />) },
      { path: 'show-boxes/:binId', element: withSuspense(<KioskShowBoxDetailPage />) },
      { path: 'search', element: withSuspense(<KioskSearchPage />) },
      { path: 'featured', element: withSuspense(<KioskFeaturedView />) },
      { path: 'shows', element: withSuspense(<KioskShowsView />) },
      { path: 'connect', element: withSuspense(<KioskConnectView />) },
      { path: 'pay', element: withSuspense(<KioskPayView />) },
      { path: 'join', element: withSuspense(<KioskJoinView />) },
    ],
  },
  {
    path: '/m',
    element: (
      <RequireAuth>
        <Suspense fallback={<AppShellSkeleton />}>
          <MobileLayout />
        </Suspense>
      </RequireAuth>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <Navigate to="/m/home" replace /> },
      { path: 'home', element: withSuspense(<MobileHome />) },
      { path: 'inventory', element: withSuspense(<MobileInventoryPage />) },
      { path: 'addcard', element: withSuspense(<MobileAddResearchPage />) },
      { path: 'sales', element: withSuspense(<MobileSalesPage />) },
      { path: 'more', element: withSuspense(<MobileMorePage />) },
      { path: 'help', element: withSuspense(<MobileHelpPage />) },
      { path: 'alerts', element: withSuspense(<MobileAlertsPage />) },
      { path: 'acquisitions', element: withSuspense(<MobileAcquisitionsPage />) },
      { path: 'events', element: withSuspense(<MobileEventsPage />) },
      { path: 'customers', element: withSuspense(<MobileCustomersPage />) },
      { path: 'consignors', element: withSuspense(<MobileConsignorsPage />) },
      { path: 'consignors/:consignorId', element: withSuspense(<MobileConsignorReportPage />) },
      { path: 'analytics', element: managerUp(<MobileAnalyticsPage />) },
      { path: 'audit', element: managerUp(<MobileAuditPage />) },
      { path: 'pl', element: ownerOnly(<MobilePlPage />) },
      { path: 'import', element: ownerOnly(<MobileImportPage />) },
      { path: 'members', element: ownerOnly(<MobileMembersPage />) },
      { path: 'settings', element: withSuspense(<MobileSettingsPage />) },
      { path: 'reports', element: managerUp(<MobileReportsPage />) },
      { path: 'reports/top-movers', element: managerUp(<MobileTopMoversPage />) },
      { path: 'bins', element: withSuspense(<MobileBinsPage />) },
      { path: 'cases', element: withSuspense(<MobileCasesPage />) },
      { path: 'binders', element: withSuspense(<MobileBindersPage />) },
      { path: 'sealed', element: withSuspense(<MobileSealedPage />) },
      { path: 'vault', element: ownerOnly(<MobileVaultPage />) },
      { path: 'lookup', element: withSuspense(<MobileLookup />) },
      { path: 'edit/:barcode', element: withSuspense(<MobileEditPage />) },
      { path: 'instances/:instanceId', element: withSuspense(<MobileCardDetailPage />) },
      { path: 'stacks/:skuStockId', element: withSuspense(<MobileStackDetailPage />) },
      { path: 'provenance/:instanceId', element: withSuspense(<ProvenancePage />) },
      { path: 'transfer', element: withSuspense(<MobileTransferPage />) },
      { path: 'show', element: withSuspense(<MobileShowPicker />) },
      { path: 'show/:eventId', element: withSuspense(<MobileShowHub />) },
      { path: 'show/:eventId/sell', element: withSuspense(<MobileQuickSell />) },
      { path: 'sell', element: withSuspense(<MobileQuickSell />) },
      { path: 'show/:eventId/buy', element: withSuspense(<MobileQuickBuy />) },
      { path: 'buy', element: withSuspense(<MobileQuickBuy />) },
      { path: 'show/:eventId/trade', element: withSuspense(<MobileQuickTrade />) },
      { path: 'trade', element: withSuspense(<MobileQuickTrade />) },
      ...prototypeRoutes,
    ],
  },
  { path: '*', element: <NotFound /> },
]);
